import { COVER_FRACTIONS } from '../engine/battlefield/coverModel';
import { PENETRATION_LIMIT } from '../engine/battlefield/lineOfSight';
import styles from '../styles/Battlefield.module.css';

/**
 * How cover and penetration are worked out, written out.
 *
 * IT LIVES IN THE LEGEND, which both modes and both roles have. It used
 * to live in the build bar - and the build bar only exists in Build mode,
 * and Build is GM-only, so no player had ever seen it and the GM only met
 * it while laying terrain out rather than while shooting. A rule nobody
 * can find is a rule nobody knows.
 *
 * Written out rather than hinted at, because neither half is guessable:
 * cover is relative to the TARGET's height, and what stops a round is how
 * much material it must cross rather than what that material is made of.
 */
export default function CoverFormula() {
  const percent = (fraction) => `${Math.round(fraction * 100)}%`;

  return (
    <div className={styles.coverFormulaBody} data-testid="cover-formula">
      <p>
        <strong>Cover</strong> is a fraction of the <strong>target&apos;s own height</strong>, not a fixed number of
        metres. Hide 60&nbsp;cm of a gaunt and you have hidden most of it; hide 60&nbsp;cm of a Dreadnought and you have
        hidden its feet.
      </p>
      <p>
        <code>hidden ÷ target height</code> → the highest threshold it reaches:
      </p>
      <ul>
        <li>
          <strong>{percent(COVER_FRACTIONS.low)}</strong> - legs · low cover
        </li>
        <li>
          <strong>{percent(COVER_FRACTIONS.half)}</strong> - legs and body · half cover
        </li>
        <li>
          <strong>{percent(COVER_FRACTIONS.threeQuarter)}</strong> - arms too · three-quarter cover
        </li>
        <li>
          <strong>{percent(COVER_FRACTIONS.full)}</strong> - head as well · full cover
        </li>
      </ul>
      <p>
        Those are the body parts a hit location roll can find, so the thresholds are not invented - they are what the
        critical tables already use.
      </p>
      <p>
        <strong>Cover is never cumulative.</strong> If the line crosses several things, the HIGHEST applies and the rest
        are ignored: two low walls are one low wall&apos;s worth of protection. The readout says which one won.
      </p>

      <p>
        <strong>Cover counts only near the target.</strong> Terrain gives cover when it stands within the table&apos;s
        reach of him (2&nbsp;m by default, measured from the edge of his footprint along the ground): a berm at the
        shooter&apos;s feet hides nobody six metres away. Farther terrain still blocks sight and still slows a round.
        Smoke hides you wherever it hangs.
      </p>

      <p>
        <strong>Whether the shot gets through at all</strong> is a different question, and the answer is how much
        material is in the way:
      </p>
      <p>
        <code>Σ (hardness × depth in metres)</code>, blocked at <strong>{PENETRATION_LIMIT}</strong>
      </p>
      <ul>
        <li>a pane of glass is a window - shoot through it</li>
        <li>two metres of it is cover you can still shoot through</li>
        <li>thirty-five metres of it is a building, and stops the shot</li>
        <li>smoke has no hardness, so it never stops a round however deep</li>
      </ul>
      <p>
        Cover AP follows the depth too, between half and double the material&apos;s hardness - a pane is not a wall, and
        a one-metre wall is exactly what it has always been.
      </p>
      <p>
        <strong>Transparency</strong> decides only what you can SEE. Anything opaque has already stopped the shot by
        blocking the line, so the rule above only ever decides the see-through cases.
      </p>
    </div>
  );
}
